import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold text-foreground mb-2">Standardly Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: December 1, 2025</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Overview</h2>
            <p className="text-muted-foreground">
              Standardly ("we," "us," or "our") respects your privacy. This policy explains how we collect, use, and protect your information when you use our website and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-foreground mb-2">Information you provide:</h3>
            <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
              <li>Name and contact information (email address, phone number)</li>
              <li>Company name and job title</li>
              <li>Account credentials</li>
              <li>Payment information (processed securely by third-party payment processors)</li>
              <li>Any other information you submit through our platform</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground mb-2">Information collected automatically:</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Device and browser information</li>
              <li>IP address</li>
              <li>Usage data and interactions with our services</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">How We Use Your Information</h2>
            <p className="text-muted-foreground mb-2">We use your information to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send you product updates, newsletters, and marketing communications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Monitor and analyze usage trends</li>
              <li>Protect against fraud and unauthorized access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Information Sharing</h2>
            <p className="text-muted-foreground mb-4">We do not sell your personal information.</p>
            <p className="text-muted-foreground mb-2">We share your information only in these limited circumstances:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong className="text-foreground">Service Providers:</strong> With third-party vendors who help us deliver our services (such as hosting, payment processing, email delivery, and analytics). These providers are contractually obligated to protect your data and use it only for the services they provide to us.</li>
              <li><strong className="text-foreground">Legal Requirements:</strong> When required by law, legal process, or to protect the rights, property, or safety of Standardly, our users, or others.</li>
              <li><strong className="text-foreground">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to you.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Data Security</h2>
            <p className="text-muted-foreground">
              We implement reasonable technical and organizational measures to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Your Rights and Choices</h2>
            <p className="text-muted-foreground mb-2">You may:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Access, correct, or delete your personal information by contacting us</li>
              <li>Unsubscribe from marketing emails using the link in any email</li>
              <li>Disable cookies through your browser settings</li>
              <li>Request data portability where applicable</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              If you are located in the European Economic Area, United Kingdom, or California, you may have additional rights under applicable law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your information for as long as your account is active or as needed to provide services, comply with legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our services are not directed to individuals under 16. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">International Data Transfers</h2>
            <p className="text-muted-foreground">
              Your information may be transferred to and processed in countries other than your own. We take steps to ensure appropriate safeguards are in place.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Contact Us</h2>
            <p className="text-muted-foreground mb-2">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">Standardly</strong><br />
              Email: <a href="mailto:privacy@standardly.com" className="text-primary hover:underline">privacy@standardly.com</a>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
